import cx from 'classnames';
import _ from 'lodash';
import React, { ReactNode, useCallback, useReducer, useRef, useEffect, useState } from 'react';

import { useResizeDetector } from '../../utils/hooks/useResizeDetector';

import SplitViewPane from './__pane/split-view__pane';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import styles from './split-view.module.scss';
import { actions, getInitialState, reducer } from './store';

interface Props {
    children: ReactNode[];
    minSizes?: number[];
    className?: string;
    direction?: 'row' | 'column';
    initialSizes?: number[];
    disableResizable?: boolean;
    extendedResizerActiveArea?: boolean;
    onResizeEnd?: () => void;
}

const mapDirectionToOffset = {
    row: 'offsetWidth',
    column: 'offsetHeight',
} as const;

const SplitView = ({
    minSizes,
    children,
    className,
    direction = 'row',
    initialSizes,
    extendedResizerActiveArea = false,
    disableResizable = false,
    onResizeEnd,
}: Props) => {
    const [state, dispatch] = useReducer(reducer, initialSizes || children.length, getInitialState);
    const getSize = useCallback((el: HTMLElement) => el[mapDirectionToOffset[direction]], [direction]);
    const ref = useRef<HTMLDivElement>(null);
    const [size] = useResizeDetector(ref, getSize);
    const [isResizing, setIsResizing] = React.useState(0);

    const [initialSizeMemo, setInitialSizeMemo] = useState(initialSizes);

    useEffect(() => {
        if (!_.isEqual(initialSizeMemo, initialSizes)) {
            setInitialSizeMemo(initialSizes);
        }
    }, [initialSizes, initialSizeMemo]);

    useEffect(() => {
        dispatch(actions.reinit({ sizes: initialSizeMemo || children.length }));
    }, [children.length, initialSizeMemo]);

    const handleResizeEnd = (index: number) => (paneSize: number) => {
        dispatch(actions.resizeEnd({ index, paneSize, size }));
        setIsResizing((i) => i - 1);

        onResizeEnd?.();
    };

    return (
        <div className={cx(styles.split, className)} ref={ref} style={{ flexDirection: direction }}>
            {children.map((child, index) => (
                <SplitViewPane
                    className={`split-view__pane_index_${index}`}
                    direction={direction}
                    extendedResizerActiveArea={extendedResizerActiveArea}
                    getSize={getSize}
                    isResizing={isResizing > 0}
                    key={index}
                    minSize={minSizes?.[index]}
                    onResize={(movement) => dispatch(actions.resize({ movement, index, size, minSizes }))}
                    onResizeEnd={handleResizeEnd(index)}
                    onResizeStart={() => setIsResizing((i) => i + 1)}
                    resizable={!disableResizable && index > 0}
                    size={(state.draggingSizes || state.sizes)[index]}
                >
                    {child}
                </SplitViewPane>
            ))}
        </div>
    );
};

export default React.memo(SplitView);
